// Copyright (c) 2026 Solartek LLC. All rights reserved.
/**
 * Short leave — hourly permissions, special reduced hours and out-of-office time (migration 20261019000310, engine 1.5.0).
 *
 *  - `time_off_types`: the organisation's permission types (personal permission, official assignment, remote work, nursing
 *    hour, disability short leave…). A type of a category that is work (OFFICIAL, REMOTE) counts as worked time by default.
 *  - `time_off_requests`: one window of one day (local times of the employee's branch, start < end within 00:00–24:00), its
 *    minutes computed by the server. Routed through the approval engine as entity SHORT_LEAVE; HR may record one on behalf of
 *    an employee (approved at once, like recorded leave).
 *  - `employee_hour_allowances`: recurring reduced hours (the nursing hour, reduced hours for persons with disabilities):
 *    minutes per working day off the start, the end, or either.
 * The attendance engine reads the approved windows and the allowances of a day: lateness / early departure inside a window is
 * excused, work-like windows are credited to the worked minutes, and out-of-office time no window covers is "lost time".
 */
import { z } from 'zod';
import { RECORD_STATUSES } from '../enums.js';
import { codeSchema, isoDateSchema, paginationQuerySchema, uuidSchema } from '../common.js';
export const TIME_OFF_CATEGORIES = ['PERSONAL', 'OFFICIAL', 'REMOTE', 'NURSING', 'DISABILITY', 'OTHER'];
/** Categories whose time is work (an official assignment, remote work): a new type of these counts as worked time by default. */
export const TIME_OFF_WORK_CATEGORIES = ['OFFICIAL', 'REMOTE'];
export const timeOffCountsAsWorkByDefault = (category) => TIME_OFF_WORK_CATEGORIES.includes(category);
/** A request is PENDING until the approval engine decides it; withdrawing (employee) or cancelling (HR) makes it CANCELLED. */
export const TIME_OFF_STATUSES = ['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'];
/** PENDING and APPROVED requests count against the monthly quota and block an overlapping request. */
export const ACTIVE_TIME_OFF_STATUSES = ['PENDING', 'APPROVED'];
export const TIME_OFF_GENDERS = ['all', 'male', 'female'];
export const HOUR_ALLOWANCE_KINDS = ['NURSING', 'DISABILITY', 'OTHER'];
/** Where the reduced minutes go: the expected start moves later, the expected end earlier, or whichever the day needs (late first). */
export const HOUR_ALLOWANCE_POSITIONS = ['START', 'END', 'EITHER'];
/** A clock time of a short-leave window: HH:mm from 00:00 to 24:00 (24:00 = the end of the day). */
export const clockTimeSchema = z.string().regex(/^(([01]\d|2[0-3]):[0-5]\d|24:00)$/, 'Expected HH:mm between 00:00 and 24:00');
/** "HH:mm" (or "HH:mm:ss", as Postgres returns a `time`) → minutes after midnight. */
export function clockToMinutes(v) {
    const [h, m] = v.split(':');
    return Number(h) * 60 + Number(m);
}
/** Minutes after midnight → "HH:mm" (1440 → "24:00"). */
export function minutesToClock(minutes) {
    const m = Math.max(0, Math.min(1440, Math.round(minutes)));
    return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}
/** Minutes of a window, or null when it is not a forward window of one day. */
export function windowMinutes(startTime, endTime) {
    const s = clockToMinutes(startTime);
    const e = clockToMinutes(endTime);
    return Number.isFinite(s) && Number.isFinite(e) && s < e && s < 1440 && e <= 1440 ? e - s : null;
}
// ----- types -----------------------------------------------------------------------------------------------------------------
export const timeOffTypeInputSchema = z.object({
    code: codeSchema,
    name: z.string().trim().min(1).max(120),
    nameAr: z.string().trim().max(120).nullable().optional(),
    category: z.enum(TIME_OFF_CATEGORIES),
    /** Counted as worked time (credited to the day's worked minutes). Omitted: true for OFFICIAL / REMOTE, false otherwise. */
    countsAsWork: z.boolean().optional(),
    /** Longest window one request may ask for. Null = no limit. */
    maxMinutesPerRequest: z.number().int().min(5).max(1440).nullable().optional(),
    /** Per employee and calendar month (pending + approved requests). Null = no limit. */
    monthlyQuotaMinutes: z.number().int().min(5).max(20_000).nullable().optional(),
    monthlyQuotaCount: z.number().int().min(1).max(100).nullable().optional(),
    /** False: a request is approved at once (no approver). */
    requiresApproval: z.boolean().optional(),
    applicableGender: z.enum(TIME_OFF_GENDERS).optional(),
    /** Offered in the employee portal; false = HR records it on the employee's behalf only. */
    portalVisible: z.boolean().optional(),
    status: z.enum(RECORD_STATUSES).optional(),
});
/**
 * PATCH body: every field optional and none defaulted (AGENTS.md Zod 4 pitfall — a one-field PATCH changes one field). The code
 * is the type's identity in the attendance trace and cannot be changed.
 */
export const timeOffTypeUpdateSchema = timeOffTypeInputSchema.omit({ code: true }).partial();
// ----- requests ----------------------------------------------------------------------------------------------------------------
const windowRefinement = (v, ctx) => {
    if (windowMinutes(v.startTime, v.endTime) === null)
        ctx.addIssue({ code: 'custom', path: ['endTime'], message: 'The end must be after the start, on the same day' });
};
/** POST /orgs/:orgId/me/time-off — the employee asks for a short leave on one day. */
export const selfTimeOffRequestSchema = z.object({
    typeId: uuidSchema,
    date: isoDateSchema,
    startTime: clockTimeSchema,
    endTime: clockTimeSchema,
    reason: z.string().trim().min(3, 'Explain the request in a few words').max(1000),
}).superRefine(windowRefinement);
/** POST /orgs/:orgId/time-off-requests — HR records a short leave for an employee (leave.manage). */
export const timeOffRequestInputSchema = z.object({
    employeeId: uuidSchema,
    typeId: uuidSchema,
    date: isoDateSchema,
    startTime: clockTimeSchema,
    endTime: clockTimeSchema,
    reason: z.string().trim().min(3).max(1000),
}).superRefine(windowRefinement);
/** Withdrawing (employee, pending only) or cancelling (HR, also approved) always says why. */
export const timeOffCancelSchema = z.object({ reason: z.string().trim().min(3).max(500) });
export const timeOffRequestListQuerySchema = paginationQuerySchema.extend({
    employeeId: uuidSchema.optional(),
    branchId: uuidSchema.optional(),
    typeId: uuidSchema.optional(),
    status: z.enum(TIME_OFF_STATUSES).optional(),
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
});
/** GET /me/time-off?month=YYYY-MM — the employee's requests of a month (default: the current one) and the month's usage. */
export const selfTimeOffQuerySchema = z.object({ month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/).optional() });
// ----- reduced hours (allowances) ------------------------------------------------------------------------------------------------
export const hourAllowanceInputSchema = z.object({
    kind: z.enum(HOUR_ALLOWANCE_KINDS),
    minutesPerDay: z.number().int().min(15).max(240),
    position: z.enum(HOUR_ALLOWANCE_POSITIONS),
    validFrom: isoDateSchema,
    /** Inclusive last day; null = open-ended (a permanent disability allowance). A nursing hour always ends. */
    validTo: isoDateSchema.nullable().optional(),
    reference: z.string().trim().max(120).nullable().optional(),
    notes: z.string().trim().max(500).nullable().optional(),
}).superRefine((v, ctx) => {
    if (v.validTo && v.validTo < v.validFrom)
        ctx.addIssue({ code: 'custom', path: ['validTo'], message: 'The last day cannot be before the first day' });
    if (v.kind === 'NURSING' && !v.validTo)
        ctx.addIssue({ code: 'custom', path: ['validTo'], message: 'A nursing hour needs its last day' });
});
/** PATCH body (explicit, no defaults): the range and the minutes may change; the kind stays. */
export const hourAllowanceUpdateSchema = z.object({
    minutesPerDay: z.number().int().min(15).max(240).optional(),
    position: z.enum(HOUR_ALLOWANCE_POSITIONS).optional(),
    validFrom: isoDateSchema.optional(),
    validTo: isoDateSchema.nullable().optional(),
    reference: z.string().trim().max(120).nullable().optional(),
    notes: z.string().trim().max(500).nullable().optional(),
});
//# sourceMappingURL=short-leave.js.map